import React, { useState, useMemo, useEffect } from 'react';
import api from '../services/api';
import { Container, Typography, Box, Button, Collapse, TextField, Autocomplete, CircularProgress, FormControlLabel, Switch } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import { debounce } from 'lodash';
import { actionButtonSx, filterCardSx, pageTitleSx } from '../styles/uiTokens';
import AcessoLiberadoList from '../components/AcessoLiberadoList';
import AcessoLiberadoForm from '../components/AcessoLiberadoForm';

function AcessosLiberadosPage() {
  const [refreshList, setRefreshList] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [selectedImovelId, setSelectedImovelId] = useState('');
  const [imovelOptions, setImovelOptions] = useState([]);
  const [loadingImoveis, setLoadingImoveis] = useState(false);
  const [selectedImovelFilter, setSelectedImovelFilter] = useState(null);

  const debouncedFetchImoveis = useMemo(() => debounce((searchTerm) => {
    setLoadingImoveis(true);
    api.get(`/imoveis?search=${encodeURIComponent(searchTerm)}&page=0&limit=20`)
      .then((response) => setImovelOptions(response.data.data || response.data))
      .catch((error) => console.error('Erro ao buscar imoveis para filtro:', error))
      .finally(() => setLoadingImoveis(false));
  }, 500), []);

  useEffect(() => () => debouncedFetchImoveis.cancel(), [debouncedFetchImoveis]);

  const handleSaved = () => {
    setRefreshList((prev) => !prev);
    setEditing(null);
    setShowForm(false);
  };

  const handleEdit = (item) => {
    setEditing(item);
    setShowForm(true);
  };

  const handleCancelEdit = () => {
    setEditing(null);
    setShowForm(false);
  };

  const handleShowForm = () => {
    setEditing(null);
    setShowForm(true);
  };

  return (
    <Container sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" gutterBottom sx={pageTitleSx}>
          Acessos Liberados
        </Typography>
        {!showForm && (
          <Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleShowForm}
              sx={{ ...actionButtonSx, mr: 1 }}
            >
              Adicionar Acesso Liberado
            </Button>
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => setShowFilters((prev) => !prev)}
            >
              Filtros
            </Button>
          </Box>
        )}
      </Box>

      {!showForm && (
        <Collapse in={showFilters}>
          <Box sx={filterCardSx}>
            <Typography variant="h6" gutterBottom>Filtros</Typography>
            <TextField
              label="Nome da pessoa"
              fullWidth
              margin="normal"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Autocomplete
              options={imovelOptions}
              getOptionLabel={(option) => `${option.descricao} - ${option.numero}` || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={selectedImovelFilter}
              onChange={(event, newValue) => {
                setSelectedImovelFilter(newValue);
                setSelectedImovelId(newValue ? newValue.id : '');
              }}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === 'input') {
                  if (newInputValue) debouncedFetchImoveis(newInputValue);
                  else setImovelOptions([]);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Imóvel (unidade)"
                  variant="outlined"
                  margin="normal"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingImoveis ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <FormControlLabel
              control={<Switch checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />}
              label="Exibir também inativos"
              sx={{ mt: 1 }}
            />
          </Box>
        </Collapse>
      )}

      {showForm && (
        <AcessoLiberadoForm
          onSaved={handleSaved}
          initialData={editing}
          onCancelEdit={handleCancelEdit}
        />
      )}

      {!showForm && (
        <AcessoLiberadoList
          refresh={refreshList}
          onEdit={handleEdit}
          onChanged={() => setRefreshList((prev) => !prev)}
          search={search}
          imovelId={selectedImovelId}
          showAll={showAll}
        />
      )}
    </Container>
  );
}

export default AcessosLiberadosPage;
